import { Link } from "react-router";
import { useStatusBar } from "@/api/queries";
import { Glyph } from "@/components/ui/Glyph";
import { clock } from "@/lib/format";
import styles from "./shell.module.css";

const DOT: Record<string, string> = { ok: "var(--ok)", warn: "var(--hum)", off: "var(--crit)" };

/** Footer: monitoring sources, audit chain, model and version. Phones show these in More. */
export function StatusBar() {
  const status = useStatusBar();
  const data = status.data;
  const sources = data?.sources ?? [];
  const unwell = sources.filter((s) => s.status !== "ok");
  // A few sources in trouble are each named; more than that reads as one line, not a wall of red.
  const summarise = unwell.length > 2;
  const allOff = summarise && unwell.length === sources.length;
  return (
    <footer className={styles.status} aria-label="System status">
      <Link to="/connections" className={styles.statusLink}>
        Sources
      </Link>
      {sources.map((s) => {
        const warn = s.status !== "ok";
        if (warn && summarise) return null;
        return (
          <span
            key={s.key}
            className={`${styles.source} ${warn ? "" : styles.statusWide}`}
            style={warn ? { color: "var(--humt)", fontWeight: 500 } : undefined}
          >
            <span
              className={styles.sourceDot}
              style={{ background: DOT[s.status] ?? "var(--dim)" }}
            />
            {warn ? `${s.name}, ${s.status_text}` : s.name}
          </span>
        );
      })}
      {summarise ? (
        <Link to="/connections" className={`${styles.source} ${styles.sourceAlert}`}>
          <span
            className={styles.sourceDot}
            style={{ background: allOff ? "var(--dim)" : "var(--hum)" }}
          />
          {allOff ? "None connected yet" : `${unwell.length} need attention`}
        </Link>
      ) : null}
      {data ? (
        <>
          <span className={styles.source} style={{ marginLeft: "auto" }}>
            {data.audit.ok ? (
              <>
                <Glyph name="check" size={12} strokeWidth={2.6} color="var(--ok)" />
                Audit chain verified {clock(data.audit.verified_at)}
              </>
            ) : (
              <span style={{ color: "var(--critt)", fontWeight: 600 }}>
                Audit chain broken at entry {data.audit.broken_at_seq}
              </span>
            )}
          </span>
          <span className={styles.statusWide}>Model: {data.model_label}</span>
          <span className={styles.statusWide}>ZeaOps {data.version}</span>
        </>
      ) : null}
    </footer>
  );
}
